import { env } from "@/lib/env";

const GIS_SCRIPT_SRC = "https://accounts.google.com/gsi/client";
const GIS_SCRIPT_ID = "google-identity-services";

type CredentialResponse = {
  credential?: string;
  select_by?: string;
};

type PromptMomentNotification = {
  isDisplayMoment?: () => boolean;
  isDisplayed?: () => boolean;
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason?: () => string;
  getSkippedReason?: () => string;
  getDismissedReason?: () => string;
};

type GoogleAccountsId = {
  initialize: (config: {
    client_id: string;
    callback: (response: CredentialResponse) => void;
    auto_select?: boolean;
    cancel_on_tap_outside?: boolean;
    context?: "signin" | "signup" | "use";
    itp_support?: boolean;
    use_fedcm_for_prompt?: boolean;
  }) => void;
  prompt: (
    momentListener?: (notification: PromptMomentNotification) => void,
  ) => void;
  renderButton: (
    parent: HTMLElement,
    options: {
      type?: "standard" | "icon";
      theme?: "outline" | "filled_blue" | "filled_black";
      size?: "large" | "medium" | "small";
      text?: "signin_with" | "signup_with" | "continue_with" | "signin";
      shape?: "rectangular" | "pill" | "circle" | "square";
      width?: number;
    },
  ) => void;
  cancel: () => void;
  disableAutoSelect: () => void;
};

declare global {
  interface Window {
    google?: {
      accounts: {
        id: GoogleAccountsId;
      };
    };
  }
}

let scriptPromise: Promise<void> | null = null;

function loadGoogleIdentityScript(): Promise<void> {
  if (typeof window === "undefined") {
    return Promise.reject(new Error("Google Identity requires a browser"));
  }

  if (window.google?.accounts?.id) {
    return Promise.resolve();
  }

  if (scriptPromise) {
    return scriptPromise;
  }

  scriptPromise = new Promise((resolve, reject) => {
    const existing = document.getElementById(GIS_SCRIPT_ID);
    if (existing) {
      existing.addEventListener("load", () => resolve(), { once: true });
      existing.addEventListener(
        "error",
        () => reject(new Error("Failed to load Google Identity Services")),
        { once: true },
      );
      return;
    }

    const script = document.createElement("script");
    script.id = GIS_SCRIPT_ID;
    script.src = GIS_SCRIPT_SRC;
    script.async = true;
    script.defer = true;
    script.onload = () => resolve();
    script.onerror = () =>
      reject(new Error("Failed to load Google Identity Services"));
    document.head.appendChild(script);
  });

  return scriptPromise;
}

/**
 * Clears Google's One Tap cooldown cookie (`g_state` / `suppressed_by_user`).
 * Without this, closing One Tap suppresses it for 2h → 1d → 1w → 4w.
 */
export function clearGoogleOneTapCooldown(): void {
  if (typeof document === "undefined") return;

  const expire = "Thu, 01 Jan 1970 00:00:00 GMT";
  const bases = [
    "g_state=; path=/; expires=" + expire,
    "g_state=; path=/; max-age=0",
  ];

  for (const base of bases) {
    document.cookie = base;
    document.cookie = `${base}; SameSite=Lax`;
  }

  // Also clear host-only variants that some browsers store.
  const host = window.location.hostname;
  if (host) {
    document.cookie = `g_state=; path=/; domain=${host}; expires=${expire}`;
    if (host.includes(".")) {
      document.cookie = `g_state=; path=/; domain=.${host}; expires=${expire}`;
    }
  }
}

export async function initializeGoogleIdentity(options: {
  onCredential: (idToken: string) => void | Promise<void>;
  onError?: (message: string) => void;
}): Promise<GoogleAccountsId> {
  if (!env.googleClientId) {
    throw new Error("VITE_GOOGLE_CLIENT_ID is not configured");
  }

  await loadGoogleIdentityScript();

  const googleId = window.google?.accounts?.id;
  if (!googleId) {
    throw new Error("Google Identity Services failed to initialize");
  }

  googleId.initialize({
    client_id: env.googleClientId,
    auto_select: false,
    cancel_on_tap_outside: false,
    context: "signin",
    itp_support: true,
    use_fedcm_for_prompt: false,
    callback: async (response) => {
      if (!response.credential) {
        options.onError?.("Google did not return a credential.");
        return;
      }
      try {
        await options.onCredential(response.credential);
      } catch (error) {
        const message =
          error instanceof Error ? error.message : "Google sign-in failed.";
        options.onError?.(message);
      }
    },
  });

  return googleId;
}

export async function promptGoogleOneTap(options: {
  onCredential: (idToken: string) => void | Promise<void>;
  onError?: (message: string) => void;
  /** One Tap is visible — hide any fallback UI. */
  onPromptDisplayed?: () => void;
  /** One Tap will not be shown, or was closed by the user. */
  onPromptUnavailable?: (reason?: string) => void;
}): Promise<void> {
  // Reset cooldown so reload / revisit can show One Tap again after cancel.
  clearGoogleOneTapCooldown();

  const googleId = await initializeGoogleIdentity(options);
  googleId.prompt((notification) => {
    // Display moment: either shown or explicitly not shown.
    if (notification.isDisplayMoment?.()) {
      if (notification.isDisplayed?.()) {
        options.onPromptDisplayed?.();
        return;
      }
      if (notification.isNotDisplayed()) {
        options.onPromptUnavailable?.(
          notification.getNotDisplayedReason?.() ?? "not_displayed",
        );
      }
      return;
    }

    if (notification.isNotDisplayed()) {
      options.onPromptUnavailable?.(
        notification.getNotDisplayedReason?.() ?? "not_displayed",
      );
      return;
    }

    if (notification.isSkippedMoment()) {
      options.onPromptUnavailable?.(
        notification.getSkippedReason?.() ?? "skipped",
      );
      return;
    }

    if (notification.isDismissedMoment()) {
      const reason = notification.getDismissedReason?.() ?? "dismissed";
      if (reason === "credential_returned") {
        return;
      }
      options.onPromptUnavailable?.(reason);
    }
  });
}

export async function renderGoogleButton(
  parent: HTMLElement,
  options: {
    onCredential: (idToken: string) => void | Promise<void>;
    onError?: (message: string) => void;
  },
): Promise<void> {
  const googleId = await initializeGoogleIdentity(options);
  parent.replaceChildren();
  googleId.renderButton(parent, {
    type: "standard",
    theme: "outline",
    size: "large",
    text: "continue_with",
    shape: "rectangular",
    width: parent.clientWidth || 320,
  });
}

export function cancelGoogleOneTap(): void {
  window.google?.accounts?.id?.cancel();
}
